/**
 * Query-intent classifier — SPD v5.81 (2026-02-14) · P0 (trio-gate).
 * ─────────────────────────────────────────────────────────────────
 * Purpose: detect whether a typed query is a commerce / transactional
 * intent (product or service purchase). Used to gate the trio strip
 * (Support Local · Online / Big Players · Compare) on BOTH the
 * primary search input and the follow-up input.
 *
 * Pure function — no side effects, no React state. Both `KnowledgeGraph
 * Page::detectQueryIntent` and `FollowUpInput` consume this so the
 * exact same rules run on both surfaces (was the bug: follow-up had
 * no gate at all).
 *
 * The regex taxonomy here is BUSINESS LOGIC not env-config, so it lives
 * in code (single source of truth) rather than in .env. If a follow-up
 * SPD wants operator-tunable keyword lists, it can be moved to a JSON
 * config later without breaking the API of this function.
 *
 * Confidence thresholds:
 *   • strong  → matches an ACT/COMPARE/NAV pattern AND non-empty subject
 *   • medium  → matches a shopping keyword AND non-empty subject
 *   • weak    → default 0 (trio HIDDEN unless env-configured
 *               `REACT_APP_TRANSACT_INTENT_MIN_CONFIDENCE` lowered)
 *
 * SPD v5.81 P1 (later) will replace this with a GLM 5.2 call for
 * category-noun-only queries like bare "shoes"; this file remains as
 * the local fast-path / fallback.
 */

// ── Pattern taxonomies ───────────────────────────────────────────────
const NAVIGATION_PATTERNS = [
  /where (?:can i |to )?(buy|find|get|purchase)/i,
  /\b(?:near me|nearby|local|closest|in my area)\b/i,
  /\b(?:stores?|shops?|locations?) (?:for|with|selling)\b/i,
  /\bfind (?:a |the )?(?:store|shop|place)\b/i,
];

const ACT_PATTERNS = [
  /^(?:buy|purchase|order|get|shop for|looking for|need|want)\b/i,
  /\b(?:for sale|on sale|deals? on|discount|prices? (?:for|on))\b/i,
  /\b(?:best (?:place|price|deal))\b/i,
  /\bsales?\b$/i,
];

const INFO_PATTERNS = [
  /\b(?:how much|what (?:is|are) the price|cost of|pricing)\b/i,
  /\b(?:reviews?|ratings?|best|top \d+)\b/i,
  /\b(?:specs?|specifications?|features?)\b/i,
];

// SPD v5.82 (2026-02-14) — every keyword now wrapped in `\b` word
// boundaries. Was a P0 classifier bug: the un-anchored `or` inside
// this list matched the substring "or" in words like "history",
// "before", "for" — so "Tell me about Canadian history" was
// classified as a Compare query and armed the trio incorrectly.
// IJ-263 (2026-08-06) — the \b fix did not stop legitimate stand-
// alone `or` in academic prose from matching. Removed `or` from the
// alternation. Second instance of the same class of false-positive.
const COMPARE_PATTERNS = [
  /\b(?:compare|vs|versus|difference between)\b/i,
  /\b(?:which is better|better than|comparison)\b/i,
  /\b(?:pros? and cons?|advantages?)\b/i,
];

// Shopping keywords: ANY of these + a valid subject also triggers gate.
// IJ-263 (2026-08-06) — dropped "looking for" from the alternation.
// The phrase has heavy dual usage across informational queries and
// was the shopping-signal that pushed the 2026-08-06 academic-
// literature query into commerce classification.
const SHOPPING_KEYWORDS = /\b(buy|purchase|shop|store|sale|deal|price|where to get|where can i find|need to buy|want to buy|for sale|on sale|near me|nearby|local|online)\b/i;

// SPD v5.86 hotfix (2026-02-14) · operator regression: "new air jordans"
// was falling through to the informational path because none of the
// launch-cadence signal words were in any pattern list. Added as its
// own dedicated pattern group (rather than diluting SHOPPING_KEYWORDS
// which would false-positive on "what's new in AI" / "learn a new
// language" / "new to programming") so that a launch-signal word MUST
// appear at the query START or as the first word after a whitespace-
// stripped strong-match verb. This catches:
//   "new air jordans"           ✓
//   "latest iPhone 16"          ✓
//   "release date for PS6"      ✓
//   "when do new jordans drop"  ✓
//   "just released Nike"        ✓
// but avoids false-positives on informational phrasing:
//   "what's new in AI"          ✗ (starts with "what's", not "new")
//   "how to build a new website" ✗ ("new" appears mid-phrase)
//   "learn a new language"      ✗ (mid-phrase)
const LAUNCH_CADENCE_PATTERNS = [
  /^(?:new|newest|latest)\s+\S+/i,                              // "new air jordans" / "latest iphone"
  /^(?:coming|coming soon|just dropped|just released)\b/i,       // "coming soon jordans" / "just dropped nike"
  /\b(?:release\s+date|drop\s+date|preorder|pre-order|restock)\b/i, // "release date for iphone"
  /\bwhen\s+(?:do|does|is|are)\s+(?:the\s+)?(?:new|latest|next)\s+\S+/i, // "when do new jordans drop"
];

// Non-product keywords: any of these EXCLUDES the query from trio gate,
// even if a shopping keyword also matched (informational overrides).
// IJ-263 (2026-08-06) — added seven academic-literature safety-net
// words after the 2026-08-06 incident: peer reviewed / peer-reviewed
// / journal / journals / philosophy / philosophical / critical realism.
const NON_PRODUCT_KEYWORDS = /\b(generate|create|make|build|write|analyze|analyse|assess|forecast|report|brief|documents?|summary|summarize|summarise|explain|what is|how to|how does|why|when|P&L|budget|financial|analysis|provide|background|strategy|develop|government|policy|initiative|economy|economic|knowledge|information|research|current|efforts|objectives|data|digital|academy|literacy|workforce|regulation|legislation|reform|peer reviewed|peer-reviewed|journal|journals|philosophy|philosophical|critical realism)\b/i;

// ── Env-configurable confidence threshold ────────────────────────────
// Default 1 == strong-match required (current behaviour). Operators can
// LOWER to 0 to also surface trio for medium-confidence (shopping-keyword
// only) queries, or RAISE if false positives become an issue.
export const MIN_TRANSACT_CONFIDENCE = (() => {
  // CRA replaces the literal `process.env.REACT_APP_*` at build time.
  // Optional chaining breaks that static replacement, so we reference
  // the var directly and guard the parse.
  const raw = String(process.env.REACT_APP_TRANSACT_INTENT_MIN_CONFIDENCE || '').trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 1;
})();

/**
 * classifyIntent(text) → {
 *   intent:          'compare' | 'navigation' | 'act' | 'information' | null,
 *   subject:         string,   // extracted product / service noun phrase
 *   isTransactional: boolean,  // trio gate result — confidence ≥ MIN
 *   confidence:      0 | 1 | 2, // 0 = none · 1 = shopping-kw-only · 2 = strong
 * }
 *
 * Rules (verbatim from prior in-place KnowledgeGraphPage logic, un-
 * modified for P0 to preserve "kind of works" behaviour on primary
 * input — this extraction is DRY-only. Follow-up now uses the same
 * result so both surfaces stay in sync.
 */
export function classifyIntent(text) {
  if (!text || text.length < 3) {
    return { intent: null, subject: '', isTransactional: false, confidence: 0 };
  }

  const lowerText = text.toLowerCase().trim();

  let intent = null;
  if (COMPARE_PATTERNS.some((p) => p.test(lowerText))) intent = 'compare';
  else if (NAVIGATION_PATTERNS.some((p) => p.test(lowerText))) intent = 'navigation';
  else if (ACT_PATTERNS.some((p) => p.test(lowerText))) intent = 'act';
  else if (INFO_PATTERNS.some((p) => p.test(lowerText))) intent = 'information';

  // Extract subject — strip common prefixes / suffixes.
  let subject = text
    .replace(/^(where can i |where to |buy |purchase |get |find |looking for |i need |i want |best |top \d+ )/i, '')
    .replace(/( near me| nearby| for sale| on sale| deals?| prices?| reviews?| local| online| stores?| shops?)$/i, '')
    .replace(/( vs | versus | or | compared to ).*/i, '')
    .trim();

  const hasShoppingIntent = SHOPPING_KEYWORDS.test(lowerText);
  // SPD v5.86 hotfix — launch-cadence patterns are as strong a signal
  // as ACT_PATTERNS ("buy X", "purchase X"). If any fires, we treat
  // the query as strong-match commerce intent so it fires the
  // compare-search directly on Enter (operator's "new air jordans"
  // regression).
  const hasLaunchCadence = LAUNCH_CADENCE_PATTERNS.some((p) => p.test(text));
  const hasNonProductIntent = NON_PRODUCT_KEYWORDS.test(lowerText);
  const strongMatch = !!intent || hasLaunchCadence;
  if (hasLaunchCadence && !intent) intent = 'act';

  let confidence = 0;
  if (!hasNonProductIntent && subject.length >= 2) {
    if (strongMatch) confidence = 2;
    else if (hasShoppingIntent) confidence = 1;
  }

  return {
    intent,
    subject: (confidence > 0) ? subject : '',
    isTransactional: confidence >= MIN_TRANSACT_CONFIDENCE,
    confidence,
  };
}

/**
 * shouldShowChipSurface(text) → boolean
 *
 * SPD v5.87.1 (2026-02-14) — cognitive-load cleanup.
 *
 * The SPD v5.83 LLM chip surface (a.k.a. "smart prompt") duplicates
 * the narrowing job that predictive completions already do — and
 * does it worse (10-15 s LLM latency, coarse category chips like
 * "Electronics" that don't actually narrow anything). Operator
 * directive (2026-02-14): "asking questions at a subtopic level
 * is not very useful eg: Electronics could be millions of items"
 * + "with both of them showing it's too much cognitive load".
 *
 * Rule (approved by operator 2026-02-14):
 *   • Transactional / launch-cadence → chips HIDDEN
 *     (completions already surface concrete product-name candidates)
 *   • Non-product intent (how-to / why / what-is / explain / …) → chips HIDDEN
 *     (user already knows what they want)
 *   • Everything else (bare nouns like "WWII", exploratory phrases
 *     like "pretty flowers that grow", ambiguous concepts) → chips SHOWN
 *     (this IS the case where LLM clarification genuinely helps)
 *   • Empty / < 3 chars → chips HIDDEN (nothing to classify yet)
 *
 * Signal is entirely local (regex-based, <1 ms) so no LLM latency
 * on the hot path.
 */
export function shouldShowChipSurface(text) {
  if (!text || text.trim().length < 3) return false;
  const c = classifyIntent(text);
  if (c.isTransactional) return false;
  // Non-product / informational cue words → treat as clear informational
  // intent, chips would be noise.
  if (NON_PRODUCT_KEYWORDS.test(text.toLowerCase())) return false;
  return true;
}

export default classifyIntent;
